// Where every topic of a project's mind map sits. Pure: give it the tree,
// each node's measured size and what's collapsed; get back boxes in "stage"
// coordinates, with the central topic (the project) centred on 0,0.
//
// 'map'   : Xmind's balanced map — main branches split between right and left.
// 'logic' : a logic chart — everything grows to the right.
import { TaskItem } from '../../../types/tasks';

export type MapMode = 'map' | 'logic';

/** Key of the central topic (the project itself, not an item). */
export const ROOT_KEY = 'root';

export interface Size {
  w: number;
  h: number;
}

export interface NodeBox {
  key: string;
  /** null for the central topic. */
  item: TaskItem | null;
  parentKey: string | null;
  x: number;
  y: number;
  w: number;
  h: number;
  /** 1 = grows right, -1 = grows left. */
  side: 1 | -1;
  /** 0 = central topic, 1 = main branch, … */
  level: number;
  /** Which main branch this is under — picks its colour. */
  branch: number;
  /** Lines folded away under a collapsed topic. */
  hiddenCount: number;
}

export interface MapLayout {
  boxes: Map<string, NodeBox>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

const GAP_FROM_ROOT = 64;
const GAP_X = 36;
const GAP_Y_MAIN = 22;
const GAP_Y = 10;

export const keyOf = (item: TaskItem) => item.ckey || String(item.id);

export function layoutMap(opts: {
  kids: Map<number | null, TaskItem[]>;
  sizeOf: (key: string) => Size;
  collapsed: Set<number>;
  mode: MapMode;
}): MapLayout {
  const { kids, sizeOf, collapsed, mode } = opts;
  const shown = (item: TaskItem) => (collapsed.has(item.id) ? [] : kids.get(item.id) || []);

  const heights = new Map<number, number>();
  const subtreeHeight = (item: TaskItem): number => {
    const cached = heights.get(item.id);
    if (cached !== undefined) return cached;
    const own = sizeOf(keyOf(item)).h;
    const children = shown(item);
    const stacked = children.reduce((sum, c) => sum + subtreeHeight(c), 0) + GAP_Y * Math.max(0, children.length - 1);
    const h = Math.max(own, stacked);
    heights.set(item.id, h);
    return h;
  };
  const countBelow = (item: TaskItem): number =>
    (kids.get(item.id) || []).reduce((n, c) => n + 1 + countBelow(c), 0);

  const boxes = new Map<string, NodeBox>();
  const rootSize = sizeOf(ROOT_KEY);
  const root: NodeBox = {
    key: ROOT_KEY, item: null, parentKey: null,
    x: -rootSize.w / 2, y: -rootSize.h / 2, w: rootSize.w, h: rootSize.h,
    side: 1, level: 0, branch: 0, hiddenCount: 0,
  };
  boxes.set(ROOT_KEY, root);

  const place = (children: TaskItem[], parent: NodeBox, side: 1 | -1, branchOf: (child: TaskItem, i: number) => number) => {
    if (!children.length) return;
    const level = parent.level + 1;
    const gapY = level === 1 ? GAP_Y_MAIN : GAP_Y;
    const gapX = level === 1 ? GAP_FROM_ROOT : GAP_X;
    const total = children.reduce((sum, c) => sum + subtreeHeight(c), 0) + gapY * (children.length - 1);
    let top = parent.y + parent.h / 2 - total / 2;
    children.forEach((child, i) => {
      const key = keyOf(child);
      const { w, h } = sizeOf(key);
      const band = subtreeHeight(child);
      const box: NodeBox = {
        key, item: child, parentKey: parent.key,
        x: side === 1 ? parent.x + parent.w + gapX : parent.x - gapX - w,
        y: top + band / 2 - h / 2,
        w, h, side, level,
        branch: branchOf(child, i),
        hiddenCount: collapsed.has(child.id) ? countBelow(child) : 0,
      };
      boxes.set(key, box);
      place(shown(child), box, side, () => box.branch);
      top += band + gapY;
    });
  };

  // Main branches: balanced map fills the right side first (in order), until
  // it holds about half the height, and the rest go left — the way Xmind lays
  // out a new map. Colours follow the branch's position, not its side.
  const main = kids.get(null) || [];
  let right = main;
  let left: TaskItem[] = [];
  if (mode === 'map' && main.length > 1) {
    const total = main.reduce((sum, c) => sum + subtreeHeight(c), 0);
    let acc = 0;
    let split = 0;
    while (split < main.length - 1 && (split === 0 || acc + subtreeHeight(main[split]) / 2 <= total / 2)) {
      acc += subtreeHeight(main[split]);
      split++;
    }
    right = main.slice(0, split);
    left = main.slice(split);
  }
  const indexOf = new Map(main.map((m, i) => [m.id, i]));
  place(right, root, 1, (c) => indexOf.get(c.id) ?? 0);
  place(left, root, -1, (c) => indexOf.get(c.id) ?? 0);

  const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  boxes.forEach((b) => {
    bounds.minX = Math.min(bounds.minX, b.x);
    bounds.minY = Math.min(bounds.minY, b.y);
    bounds.maxX = Math.max(bounds.maxX, b.x + b.w);
    bounds.maxY = Math.max(bounds.maxY, b.y + b.h);
  });
  return { boxes, bounds };
}

/** A curved connector from a topic to one of its sub-topics. */
export function connectorPath(parent: NodeBox, child: NodeBox): string {
  const px = child.side === 1 ? parent.x + parent.w : parent.x;
  const py = parent.y + parent.h / 2;
  const cx = child.side === 1 ? child.x : child.x + child.w;
  const cy = child.y + child.h / 2;
  const mx = (px + cx) / 2;
  return `M${px} ${py} C${mx} ${py}, ${mx} ${cy}, ${cx} ${cy}`;
}
